import { message, notification, Modal } from 'ant-design-vue';
import { NotificationArgsProps } from 'ant-design-vue/lib/notification';
import { ModalFuncProps } from 'ant-design-vue/lib/modal/Modal';

import { TextEnum, RouterEnum } from '@interface/public';
import Config from '@constant/config';
import Application from '@constant/application';
import RE from '@module/regexp';
import Check from '@module/check';

/**
 * 函数
 */
export default class FN {
  public static readonly Calc = { // 精准计算
    /**
     * 加法
     * @param {number} n1 数字1
     * @param {number} n2 数字2
     * @param {number} decimal 小数位数
     * @return {number} 结果
     */
    add(n1: number, n2: number, decimal: number): number {
      return $Numeral($MathJS.add($MathJS.bignumber(n1), $MathJS.bignumber(n2)))
        .format(`0.${ $Lodash.times(decimal, () => 0).join('') }`);
    },
    
    /**
     * 减法
     * @param {number} n1 数字1
     * @param {number} n2 数字2
     * @param {number} decimal 小数位数
     * @return {number} 结果
     */
    sub(n1: number, n2: number, decimal: number): number {
      return FN.Calc.add(n1, -Number(n2), decimal);
    },
    
    /**
     * 获取随机数
     * @param {number} r1 范围1
     * @param {number} r2 范围2
     * @return {number} 返回随机数
     */
    random(r1: number, r2: number): number {
      return Math.floor(Math.random() * (r2 - r1 + 1) + r1);
    }
  };
  public static readonly Agent = { // 代理信息
    /**
     * 客户端
     * @return {string} 返回客户端信息
     */
    client(): string {
      const agent = navigator.userAgent.toLowerCase(),
        client = Object.keys(RE.Client).find((v: string) => {
          return (RE.Client as any)[v].test(agent);
        }) || agent;
      
      client === agent && console.log('未知客户端：' + agent);
      
      return client;
    },
    
    /**
     * 系统
     * @return {string} 返回系统信息
     */
    system(): string {
      const agent = navigator.userAgent.toLowerCase(),
        system = Object.keys(RE.System).find((v: string) => {
          return (RE.System as any)[v].test(agent);
        }) || agent;
      
      system === agent && console.log('未知系统：' + agent);
      
      return system;
    },
    
    /**
     * 浏览器
     * @return {string} 返回浏览器信息
     */
    browser(): string {
      const agent = navigator.userAgent.toLowerCase(),
        browser = Object.keys(RE.Browser).find((v: string) => {
          return (RE.Browser as any)[v].test(agent);
        }) || agent;
      
      browser === agent && console.log('未知浏览器：' + agent);
      
      return browser;
    }
  };
  public static readonly Cookie = { // Cookie
    /**
     * 设置
     * @param {object} o 一维对象
     * @param {number} time 时间（小时）
     * @param {string} path 地址
     * @param {string} domain 域名
     */
    set(o: any, time: number = 0, path: string = '', domain: string = ''): void {
      const expire = time > 0 ? `expires=${ new Date(new Date().getTime() + time * Check.Constraint.Hour * Check.Constraint.Milli).toUTCString() };` : '';
      
      path !== '' && (path = `path=${ path };`);
      domain !== '' && (domain = `domain=${ domain };`);
      
      if (o === undefined || Object.keys(o).length === 0) return;
      
      Object.keys(o).forEach((v: string) => {
        document.cookie = `${ v }=${ encodeURIComponent(o[v]) };${ expire + path + domain }`;
      });
    },
    
    /**
     * 获取
     * @param {string} key 键
     * @return {string|null} 值
     */
    get(key: string): string | null {
      const regExp = new RegExp(`(?:(?:^|.*;\\s*)${ key }\\s*\=\\s*([^;]*).*$)|^.*$`),
        value: any = document.cookie.match(regExp);
      
      return (!value || value[1] === undefined) ? null : decodeURIComponent(value[1]);
    },
    
    /**
     * 删除
     * @param {string[]} array 字符串数组
     * @param {string} path 地址
     * @param {string} domain 域名
     */
    del(array: string[], path: string = '', domain: string = ''): void {
      const expire = `expires=${ new Date(new Date().getTime() - 1).toUTCString() };`;
      
      path !== '' && (path = `path=${ path };`);
      domain !== '' && (domain = `domain=${ domain };`);
      
      if (array === undefined || array.length === 0) return;
      
      array.forEach((v: string) => {
        document.cookie = `${ v }=;${ expire + path + domain }`;
      });
    }
  };
  public static readonly LocalStorage = { // LocalStorage
    /**
     * 设置
     * @param {object} o 一维对象
     */
    set(o: any): void {
      if (o === undefined || Object.keys(o).length === 0) return;
      
      Object.keys(o).forEach((v: string) => {
        localStorage.setItem(v, encodeURIComponent(o[v]));
      });
    },
    
    /**
     * 获取
     * @param {string} key 键
     * @return {string|null} 值
     */
    get(key: string): string | null {
      const value = localStorage.getItem(key);
      return value ? decodeURIComponent(value) : null;
    },
    
    /**
     * 删除
     * @param {string[]} array 字符串数组
     */
    del(array: string[]): void {
      array.forEach((v: string) => {
        localStorage.removeItem(v);
      });
    },
    
    /**
     * 清理
     */
    clear(): void {
      localStorage.clear();
    }
  };
  public static readonly SessionStorage = { // SessionStorage
    /**
     * 设置
     * @param {object} o 一维对象
     */
    set(o: any): void {
      if (o === undefined || Object.keys(o).length === 0) return;
      
      Object.keys(o).forEach((v: string) => {
        sessionStorage.setItem(v, encodeURIComponent(o[v]));
      });
    },
    
    /**
     * 获取
     * @param {string} key 键
     * @return {string|null} 值
     */
    get(key: string): string | null {
      const value = sessionStorage.getItem(key);
      return value ? decodeURIComponent(value) : null;
    },
    
    /**
     * 删除
     * @param {string[]} array 字符串数组
     */
    del(array: string[]): void {
      array.forEach((v: string) => {
        sessionStorage.removeItem(v);
      });
    },
    
    /**
     * 清理
     */
    clear(): void {
      sessionStorage.clear();
    }
  };
  public static readonly Url = { // Url
    /**
     * 设置
     * @param {*} o 参数一维对象
     * @param {string} url Url字符串
     * @return {string} 返回Url
     */
    set(o: any, url: string = location.href): string {
      const hash = url.split('#')[1] || '';
      let href = url.split('?')[0] || '',
        search = url.split('?')[1] || '',
        param = '';
      
      if (Object.keys(o).length === 0) return url;
      
      if (hash !== '') {
        href = href.split('#')[0];
        search = search.split('#')[0];
      }
      
      Object.keys(o).forEach((v: string) => {
        const regExp = new RegExp(`(?:&|/?)${ v }=([^&$]+)`);
        let hasParam: any = '';
        
        search !== '' && (hasParam = search.match(regExp));
        
        if (hasParam && hasParam[1] !== undefined) {
          search = search.replace(hasParam[1], o[v])
        } else {
          param !== '' && (param += '&');
          param += v + '=' + o[v];
        }
      });
      
      if (search !== '') {
        if (search[0] === '&') {
          search = '?' + param + search;
        } else {
          search = '?' + param + '&' + search;
        }
      } else {
        search = '?' + param;
      }
      
      hash !== '' && (search += '#' + hash);
      
      return href + search;
    },
    
    /**
     * 获取
     * @param {string} key 键
     * @param {string} url Url地址
     * @return {string|null} 值
     */
    get(key: string, url: string = location.search.substring(1)): string | null {
      const regExp = new RegExp(`(?:&|/?)${ key }=([^&$]+)`);
      let value: any = null;
      
      url.includes('?') && (url = url.split('?')[1]);
      url.includes('#') && (url = url.split('#')[0]);
      value = url.match(regExp);
      
      return (!value || value[1] === undefined) ? null : decodeURIComponent(value[1]);
    },
    
    /**
     * 删除
     * @param {array} array 参数数组
     * @param {string} url Url字符串
     * @return {string} 返回Url
     */
    del(array: string[], url: string = location.href): string {
      const hash = url.split('#')[1] || '';
      let href = url.split('?')[0] || '',
        search = url.split('?')[1] || '';
      
      if (array.length === 0) return url;
      
      if (hash !== '') {
        href = href.split('#')[0];
        search = search.split('#')[0];
      }
      
      array.forEach((v: string) => {
        const regExp = new RegExp(`(^|&)${ v }=([^&]*)(&|$)`);
        let hasParam: any = '';
        
        search !== '' && (hasParam = search.match(regExp));
        (hasParam && hasParam[0] !== undefined) && (search = search.replace(hasParam[0], ''));
      });
      
      search[0] === '&' && (search = search.slice(1));
      search !== '' && (search = '?' + search);
      hash !== '' && (search += '#' + hash);
      
      return href + search;
    },
    
    /**
     * 获取全部参数
     * @param {string} url Url
     * @return {array|null} 返回Url参数数组
     */
    all(url: string = location.search.substring(1)): any {
      const param: any = {};
      let urlList: any = url;
      
      if (!url || !url.includes('=')) return param;
      
      url.includes('?') && (urlList = url.split('?')[1]);
      url.includes('#') && (urlList = urlList.split('#')[0]);
      
      if (!urlList) return param;
      
      urlList = urlList.split('&');
      
      urlList.forEach((v: string) => {
        const p = v.split('=');
        p[0] && (param[p[0]] = decodeURIComponent(p[1]));
      });
      
      return param;
    },
    
    /**
     * 获取哈希值
     * @return {string} 返回Url的hash值
     */
    hash(): string {
      return decodeURIComponent(location.hash).substring(1);
    },
    
    /**
     * 参数化
     * @param {*} o 对象
     * @param {boolean} hasQuestion 是否添加？
     * @return {string} 参数
     */
    toParam(o: any, hasQuestion: boolean = false): string {
      let param = '';
      
      for (const key in o) param += (param === '' ? '' : '&') + key + '=' + o[key];
      
      return hasQuestion ? '?' + param : param;
    },
    
    /**
     * 判断是否是外链
     * @param {string} url 链接
     * @return {boolean} 是否是外链
     */
    isLink(url: string): boolean {
      return url.includes('http://') || url.includes('https://');
    }
  };
  public static readonly Array = { // 数组
  };
  public static readonly Object = { // 对象
    /**
     * 同步
     * @param {*} target 目标对象
     * @param {*} source 源对象
     */
    synchronize(target: any, source: any): void {
      Object.keys(target).forEach((v: string) => {
        source[v] !== undefined && (target[v] = source[v]);
      });
    }
  };
  public static readonly Time = { // 时间
    /**
     * 时间戳
     * @param {number|string} time 时间
     * @return {number} 时间戳
     */
    timestamp(time: number | string): number {
      return $Moment(time).unix();
    },
    
    /**
     * 格式化
     * @param {number|string} time 时间
     * @param {boolean} isAccurateTime 是否精确到时间
     * @return {string} 格式化后的日期
     */
    format(time: number | string, isAccurateTime: boolean = false): string {
      return $Moment(time).format('YYYY-MM-DD' + (isAccurateTime ? ' HH:mm:ss' : ''));
    }
  };
  public static readonly Color = { // 颜色
    /**
     * 16进制转RGB
     * @param {string} hex 16进制
     * @param {boolean} isFormat 是否带格式
     * @return {string} rgb
     */
    hexToRGB(hex: string, isFormat: boolean = true): string {
      const color = [];
      
      if (!RE.Color.HEX.test(hex)) return hex;
      
      for (let i = 1; i <= 6; i += 2)
        color.push(parseInt('0x' + hex.slice(i, i + 2)));
      
      return isFormat
        ? `rgb(${ color.join(',') })`
        : color.join(',');
    },
    
    /**
     * RGB转16进制
     * @param {string} rgb RGB
     * @param {boolean} isFormat 是否带格式
     * @return {string} hex
     */
    rgbToHEX(rgb: string, isFormat: boolean = true): string {
      const color = rgb.replace(/[rR][gG][Bb]|(\()|(\))*/g, '').split(',');
      
      return (isFormat ? '#' : '') +
             color.reduce((t: string, v: string, i) => {
               let hex = Number(color[i]).toString(16);
               hex.length < 2 && (hex = `0${ hex }`);
               return t + hex;
             }, '');
    }
  };
  public static readonly File = { // 文件
    /**
     * 加载文件
     * @param {string} url 地址
     */
    async load(url: string): Promise<any> {
      const urlList: string[] = url.split('.');
      let suffix: string = urlList[urlList.length - 1],
        dom: any = null,
        position: string = '';
      
      suffix = suffix.split('#')[0];
      suffix = suffix.split('?')[0];
      
      switch (suffix) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          position = 'body';
          dom = document.createElement('img');
          dom.style.position = 'absolute';
          dom.style.zIndex = -999;
          dom.style.top = -999;
          dom.style.left = -999;
          dom.style.opacity = 0;
          dom.src = url;
          break;
        case 'css':
          position = 'head';
          dom = document.createElement('link');
          dom.setAttribute('ref', 'stylesheet');
          dom.href = url;
          break;
        case 'js':
        case 'php':
          position = 'body';
          dom = document.createElement('script');
          dom.setAttribute('type', 'text/javascript');
          dom.setAttribute('defer', 'true');
          dom.src = url;
          break;
      }
      
      return new Promise((resolve: any, reject: any) => {
        dom.onload = () => {
          resolve(dom);
        };
        
        dom.onerror = () => {
          reject();
        };
        
        switch (position) {
          case 'head':
            document.head.appendChild(dom);
            break;
          case 'body':
            document.body.appendChild(dom);
            break;
        }
      });
    },
    
    /**
     * 下载CSV
     * @param {string} name 文件名
     * @param {string[][]} dataList 二维数组列表
     */
    downloadCSV(name: string, dataList: string[][]): void {
      const dom = document.createElement('a'),
        blob = new Blob([
          '\uFEFF',
          dataList.reduce((t: string, v: string[], i: number) => t + v.join(',') + ((i + 1 < dataList.length) ? '\r\n' : ''), '')
        ], { type: 'text/csv' });
      
      dom.style.display = 'none';
      dom.download = `${ name }.csv`;
      dom.href = URL.createObjectURL(blob);
      dom.click();
      URL.revokeObjectURL(dom.href);
    }
  };
  public static readonly Math = { // 数学
    /**
     * 贝塞尔曲线-1阶公式
     * @param {number} t 当前百分比
     * @param {number[]} p1 起点坐标
     * @param {number[]} p2 终点坐标
     * @return {number[]} 坐标
     */
    oneBezier(t: number, p1: number[], p2: number[]): number[] {
      const [ x1, y1 ] = p1,
        [ x2, y2 ] = p2;
      
      return [
        x1 + (x2 - x1) * t,
        y1 + (y2 - y1) * t
      ];
    },
    
    /**
     * 二阶贝塞尔
     * @param {number} t 当前百分比
     * @param {number[]} p1 起点坐标
     * @param {number[]} p2 终点坐标
     * @param {number[]} cp 控制点
     * @return {number[]} 坐标
     */
    twoBezier(t: number, p1: number[], p2: number[], cp: number[]): number[] {
      const [ x1, y1 ] = p1,
        [ x2, y2 ] = p2,
        [ cx, cy ] = cp;
      
      return [
        (1 - t) * (1 - t) * x1 + 2 * t * (1 - t) * cx + t * t * x2,
        (1 - t) * (1 - t) * y1 + 2 * t * (1 - t) * cy + t * t * y2
      ];
    },
    
    /**
     * @desc 三阶贝塞尔
     * @param {number} t 当前百分比
     * @param {number[]} p1 起点坐标
     * @param {number[]} p2 终点坐标
     * @param {number[]} cp1 控制点1
     * @param {number[]} cp2 控制点2
     * @return {number[]} 坐标
     */
    threeBezier(t: number, p1: number[], p2: number[], cp1: number[], cp2: number[]): number[] {
      const [ x1, y1 ] = p1,
        [ x2, y2 ] = p2,
        [ cx1, cy1 ] = cp1,
        [ cx2, cy2 ] = cp2;
      
      return [
        x1 * (1 - t) * (1 - t) * (1 - t) +
        3 * cx1 * t * (1 - t) * (1 - t) +
        3 * cx2 * t * t * (1 - t) +
        x2 * t * t * t,
        y1 * (1 - t) * (1 - t) * (1 - t) +
        3 * cy1 * t * (1 - t) * (1 - t) +
        3 * cy2 * t * t * (1 - t) +
        y2 * t * t * t
      ];
    }
  };
  
  /**
   * 监听屏幕尺寸变化
   * @param {function} callback 回调
   * @param {number} time 间隔时间
   */
  public static Resize(callback: Function, time: number = 100): void {
    const resize = () => {
      if (setTime) clearTimeout(setTime);
      setTime = setTimeout(callback, time);
    };
    let setTime: number = 0;
    
    resize();
    addEventListener('pageshow', (e: PageTransitionEvent) => { // 屏幕显示
      e.persisted && resize();
    });
    addEventListener('resize', resize); // 屏幕尺寸变化
    'onorientationchange' in window && addEventListener('orientationchange', resize); // 屏幕旋转
  }
  
  /**
   * 监听陀螺仪
   * @param {function} callback 回调
   * @param {number} time 间隔时间
   */
  public static Gyroscope(callback: Function, time: number = 50): void {
    let openGyroscope = true,
      setTime: any = null;
    
    'DeviceOrientation' in window && addEventListener('deviceorientation', (e: DeviceOrientationEvent) => {
      if (!openGyroscope) return;
      openGyroscope = false;
      setTime = setTimeout(() => {
        openGyroscope = true;
      }, time);
      
      callback({
        absolute: e.absolute,
        alpha: parseInt(String(e.alpha), 10),
        beta: parseInt(String(e.beta), 10),
        gamma: parseInt(String(e.gamma), 10)
      });
    });
  }
  
  /**
   * 切换全屏
   * @param {HTMLElement} dom 全屏的Dom
   */
  public static async SwitchFullScreen(dom: HTMLElement): Promise<any> {
    if (!document.fullscreenEnabled) {
      FN.Message('warning', {
        content: '当前浏览器不支持全屏模式，请更换浏览器重试'
      });
      return;
    }
    
    !document.fullscreenElement
      ? await dom.requestFullscreen()
      : await document.exitFullscreen();
  }
  
  /**
   * 复制至剪贴板
   * @param {string} content 内容
   */
  public static CopyClipboard(content: string): void {
    const textarea = document.createElement('textarea');
    
    textarea.style.position = 'absolute';
    textarea.style.top = '-999px';
    textarea.style.left = '-999px';
    textarea.style.width = '0px';
    textarea.style.height = '0px';
    textarea.style.opacity = '0';
    textarea.value = content;
    document.body.appendChild(textarea);
    
    textarea.select();
    document.execCommand('copy', true);
    textarea.remove();
    
    FN.Message('success', {
      content: '复制至剪贴板成功'
    });
  }
  
  /**
   * 等待时间
   * @param {number} time 等待时间（毫秒）
   */
  public static WaitTime(time: number): Promise<any> {
    return new Promise((resolve: Function) => {
      setTimeout(resolve, time);
    });
  }
  
  /**
   * 清理定时器
   * @param {any} setTime 定时器对象
   */
  public static ClearSetTime(setTime: any): void {
    $Lodash.forIn(setTime, function (value: number, key: string) {
      if (value === 0) return;
      clearTimeout(value);
      clearInterval(value);
      setTime[key] = 0;
    });
  }
  
  /**
   * 重试
   * @param {Function} callback 回调函数
   * @param {number} t 重试次数
   */
  public static async Retry(callback: Function, t: number = 3): Promise<any> {
    let times: number = 0;
    
    return new Promise(async (resolve: Function, reject: Function) => {
      const loop = async () => {
        try {
          resolve(await callback());
        }
        catch (e) {
          times++;
          
          times < t
            ? await loop()
            : reject('重试失败');
        }
      };
      
      await loop();
    });
  }
  
  /**
   * 获取Table开启滚动宽度
   * @param {any[]} columnList Table列表
   * @param {boolean} isNumber 是否返回数字
   * @return {{x:number}} 滚动对象
   */
  public static GetTableScroll(columnList: any[], isNumber: boolean = false): { x: number } | number {
    const x = columnList.reduce((total: number, current: any) => {
      return total + (current.children ? FN.GetTableScroll(current.children, true) : current.width);
    }, 0);
    
    return isNumber ? x : { x };
  }
  
  /**
   * 隐藏关键信息
   * @param {string} info 信息
   * @return {string} 隐藏后的信息
   */
  public static HideFullInfo(info: string): string {
    return `${ info.slice(0, 4) }******${ info.slice(-4) }`;
  }
  
  /**
   * 平滑过渡
   * @param {*} config 开始数值
   */
  public static SmoothTransition(
    config: {
      start: number,
      end?: number,
      precision?: number,
      time?: 300,
      callback: Function
    }
  ): void {
    const precision = Math.pow(10, config.precision || 2),
      start = config.start * precision,
      end = (config.end || 0) * precision,
      speed = (end - start) / 10;
    let current = start + speed;
    
    current += speed;
    Math.abs(current - end) <= 1 && (current = end);
    
    requestAnimationFrame(() => {
      const next = current / precision;
      
      config.callback(next);
      current != end && FN.SmoothTransition({
        ...config,
        start: next
      });
    });
  }
  
  /**
   * 消息
   * @param {'info'|'success'|'error'|'warning'|'loading'} type 类型
   * @param {{content:string,duration?:number,onClose?:()=>void}} config 配置
   */
  public static Message(
    type: 'info' | 'success' | 'error' | 'warning' | 'loading',
    config: {
      content: string,
      duration?: number,
      onClose?: () => void
    }
  ): void {
    console.log(`${ Config.Console.log }消息：`, type, JSON.stringify(config));
    
    message[type](config.content, config.duration || 5, config.onClose || null);
  }
  
  /**
   * 通知
   * @param {'info'|'success'|'error'|'warning'} type 类型
   * @param {NotificationArgsProps} config 配置
   */
  public static Notification(
    type: 'info' | 'success' | 'error' | 'warning',
    config: NotificationArgsProps
  ): void {
    console.log(`${ Config.Console.log }通知：`, type, JSON.stringify(config));
    
    config.duration === undefined && (config.duration = 5);
    notification[type](config);
  }
  
  /**
   * 对话框
   * @param {'info'|'success'|'error'|'warning'|'confirm'} type 类型
   * @param {ModalFuncProps} config 配置
   */
  public static Modal(
    type: 'info' | 'success' | 'error' | 'warning' | 'confirm',
    config: ModalFuncProps
  ): void {
    console.log(`${ Config.Console.log }对话框：`, type, JSON.stringify(config));
    
    Modal[type](config);
  }
  
  /**
   * 校验表单
   * @param {*} error 错误参数
   * @return {boolean} 是否校验成功
   */
  public static Validate(error: any): boolean {
    error && FN.Message('warning', {
      content: TextEnum.ErrorCheckFail + '：' +
               Object.keys(error).map(v => error[v].label).join(' | ')
    });
    return !!error;
  }
  
  /**
   * 信息不完整
   * @param {RouterEnum} router 路由
   */
  public static async InfoIncomplete(router: RouterEnum): Promise<any> {
    FN.Message('warning', {
      content: '参数错误'
    });
    await Application.Router.push({
      path: router
    });
  }
  
  /**
   * 格式化邮箱
   * @param {string} value 值
   * @return {{value:string}[]} 带邮箱后缀的列表
   */
  public static FormatMail(value: string): { value: string }[] {
    return $Check.Suffix.Mail
      .map((v: string) => {
        return value === '' || value.indexOf('@') >= 0
          ? null
          : { value: `${ value }@${ v }` };
      })
      .filter(v => v);
  }
}